import React from "react";
import {
  Settings,
  LogOut,
  Users,
  Package,
  GraduationCap,
  DollarSign,
} from "lucide-react";
import Button from "./ui/Button";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link, useLocation, useSearchParams } from "react-router-dom";

const Header = () => {
  const { user, logout, isAdmin, isOwner } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Check if we're on products page with brand filter
  const isProductsWithBrand = location.pathname === "/products" && searchParams.get("brand");
  
  // Determine back button text and destination
  const getBackButton = () => {
    if (location.pathname === "/dashboard") {
      return null;
    } else if (isProductsWithBrand) {
      return {
        text: "← Back to Brands",
        destination: "/brands"
      };
    } else if (location.pathname.startsWith("/admin-settings") ||
               location.pathname.startsWith("/users") ||
               location.pathname.startsWith("/payout-requesters") ||
               location.pathname.startsWith("/brands")) {
      return {
        text: "← Back to Dashboard",
        destination: "/dashboard"
      };
    }
    return null;
  };

  const backButton = getBackButton();

  return (
    <header className="bg-white/5 backdrop-blur-2xl border-b border-white/10 sticky top-0 z-20 shadow-2xl relative overflow-visible">
      {/* Glass morphism background */}
      <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-white/10"></div>

      {/* Animated glow effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#B8460E]/5 via-transparent to-[#B8460E]/5 animate-pulse"></div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 relative z-10">
        <div className="flex justify-between items-center h-16 sm:h-20">
          <div className="flex items-center">
            <Link to="/dashboard" className="flex items-center space-x-4">
              <div className="w-24 h-14 sm:w-28 sm:h-20 rounded-xl shadow-xl relative group overflow-hidden">
                {/* Removed hover shine effect */}
                <img
                  src="/whiteolympus.png"
                  alt="White Olympus Logo"
                  className="w-full h-full object-contain relative z-10"
                />
              </div>
            </Link>
            <div className="hidden sm:block border-l border-white/10 h-10 mx-6"></div>
            {location.pathname === "/dashboard" ? (
              <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                Affiliate Performance
              </h1>
            ) : backButton ? (
              <Link
                to={backButton.destination}
                className="text-gray-300 hover:text-white hover:underline text-lg"
              >
                {backButton.text}
              </Link>
            ) : null}
          </div>
          {/* Right actions */}
          <div className="hidden sm:flex items-center space-x-2">
            {(isAdmin || isOwner) && (
              <>
                <Button
                  variant="ghost"
                  size="default"
                  onClick={() => {
                    if (location.pathname.startsWith("/admin-settings")) {
                      // If already on the admin settings page, navigate back to dashboard
                      navigate("/dashboard");
                    } else {
                      // Otherwise, go to admin settings
                      navigate("/admin-settings");
                    }
                  }}
                  className="rounded-xl text-white/70 hover:text-white hover:bg-white/10 border border-white/20"
                >
                  <Settings className="h-5 w-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="default"
                  onClick={() => navigate("/users")}
                  className="rounded-xl text-white/70 hover:text-white hover:bg-white/10 border border-white/20"
                >
                  <Users className="h-5 w-5" />
                  <span className="ml-2 hidden sm:inline">Users</span>
                </Button>
                <Button
                  variant="ghost"
                  size="default"
                  onClick={() => navigate("/brands")}
                  className="rounded-xl text-white/70 hover:text-white hover:bg-white/10 border border-white/20"
                >
                  <Package className="h-5 w-5" />
                  <span className="ml-2 hidden sm:inline">Brands</span>
                </Button>
                {/**
                 * Temporarily disabled Education navigation.
                 * Keep code for future re-enable.
                 */}
                {false && (
                  <Button
                    variant="ghost"
                    size="default"
                    onClick={() => navigate("/education-management")}
                    className="rounded-xl text-white/70 hover:text-white hover:bg-white/10 border border-white/20"
                  >
                    <GraduationCap className="h-5 w-5" />
                    <span className="ml-2 hidden sm:inline">Education</span>
                  </Button>
                )}
              </>
            )}
            {user && (
              <Button
                variant="ghost"
                size="default"
                onClick={handleLogout}
                className="rounded-xl text-white/70 hover:text-white hover:bg-white/10 border border-white/20"
                title="Logout"
              >
                <LogOut className="h-5 w-5" />
                <span className="ml-2 hidden sm:inline">Logout</span>
              </Button>
            )}
          </div>
          {/* Mobile hamburger */}
          <div className="sm:hidden">
            <details className="relative z-50">
              <summary className="list-none cursor-pointer flex items-center justify-center w-10 h-10 rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 text-white">
                ☰
              </summary>
              <div className="absolute right-0 mt-2 w-56 bg-black/90 backdrop-blur-xl border border-white/20 rounded-xl shadow-2xl p-2 z-[100]">
                {isAdmin && (
                  <div className="space-y-1">
                    <button
                      onClick={() => navigate("/admin-settings")}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/10 text-white flex items-center gap-2"
                    >
                      <Settings className="h-4 w-4" /> Admin Settings
                    </button>
                    <button
                      onClick={() => navigate("/users")}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/10 text-white flex items-center gap-2"
                    >
                      <Users className="h-4 w-4" /> Users
                    </button>
                    <button
                      onClick={() => navigate("/brands")}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/10 text-white flex items-center gap-2"
                    >
                      <Package className="h-4 w-4" /> Brands
                    </button>
                    {false && (
                      <button
                        onClick={() => navigate("/education-management")}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/10 text-white flex items-center gap-2"
                      >
                        <GraduationCap className="h-4 w-4" /> Education
                      </button>
                    )}
                  </div>
                )}
                {user && (
                  <button
                    onClick={handleLogout}
                    className="mt-1 w-full text-left px-3 py-2 rounded-lg hover:bg-white/10 text-white flex items-center gap-2"
                  >
                    <LogOut className="h-4 w-4" /> Logout
                  </button>
                )}
              </div>
            </details>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
